import { autorun } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { AdvertisingStore } from 'stores/advertising-store';
import { ClickTripzModel } from 'models/common/ads-types';
import { Context } from 'bernie-context';
import { mapContextData, privacyTrackingConsent } from 'utils/meso-utils';
import { SearchCriteriaModel } from 'src/common/stores/models/search-criteria-model';
import { ErrorBoundary } from 'bernie-view';

interface ClickTripzProps {
  advertising?: AdvertisingStore;
  context?: Context;
  hotel: { hotelId: string; hotelName: string };
  referrerInfo: { referrerChannel: string };
  regionInfo: { city: string; state: string; countryCode: string };
  searchCriteria: SearchCriteriaModel;
  sharedGuids: { publisherSessionId: string };
}

@inject('context', 'advertising')
@observer
export class ClickTripz extends React.Component<ClickTripzProps> {
  private halt: () => any;

  constructor(props) {
    super(props);

    this.mapSearchCriteriaData.bind(this);
  }

  private mapSearchCriteriaData(searchCriteriaModel) {
    if (searchCriteriaModel) {
      const startDatePart = searchCriteriaModel.startDate ? searchCriteriaModel.startDate.match(/\d+/g) : '';
      const endDatePart = searchCriteriaModel.endDate ? searchCriteriaModel.endDate.match(/\d+/g) : '';
      const aRoom = [];
      const nRoomCount = searchCriteriaModel.rooms ? searchCriteriaModel.rooms.length : 0;

      for (let i = 0; i < nRoomCount; i++) {
        const oRoomDetails = {};

        const aChildrenModel = searchCriteriaModel.rooms[i].children;
        const infantCount = aChildrenModel.filter(child => child.age === 0).length;
        const nonInfantCount = aChildrenModel.length - infantCount;

        oRoomDetails['adults'] = searchCriteriaModel.rooms[i].adults;
        oRoomDetails['children'] = nonInfantCount;
        oRoomDetails['infants'] = infantCount;

        aRoom.push(oRoomDetails);
      }

      return {
        destinationId: searchCriteriaModel.regionId,
        checkInDate: startDatePart ? startDatePart[1] + '/' + startDatePart[2] + '/' + startDatePart[0] : '',
        checkOutDate: endDatePart ? endDatePart[1] + '/' + endDatePart[2] + '/' + endDatePart[0] : '',
        rooms: aRoom
      };
    }

    return {
      destinationId: '',
      checkInDate: '',
      checkOutDate: '',
      rooms: []
    };
  }

  private shouldRender() {
    return (
      this.props.context &&
      this.props.context.deviceInformation &&
      !this.props.context.deviceInformation.mobile &&
      this.props.context.locale === 'en_US' &&
      this.props.context.site &&
      this.props.context.site.eapid === 0 &&
      this.props.context.site.tpid === 1
    );
  }

  public componentDidMount() {
    const fReload = '_CTZ_RELOAD';
    const sReload = '_CTZ';

    if (this.shouldRender()) {
      let clicktripzData: ClickTripzModel = {};

      this.halt = autorun(() => {
        const { advertising, context, regionInfo, searchCriteria, sharedGuids, referrerInfo, hotel } = this.props;

        let property = {};

        if (hotel) {
          property = { id: hotel.hotelId, name: hotel.hotelName };
        }

        const searchCriteriaData = this.mapSearchCriteriaData(searchCriteria);
        const contextData = mapContextData(context);

        const updatedClickTripzData = {
          ...contextData,
          ...searchCriteriaData
        };

        let searchData: any = {
          checkInDate: updatedClickTripzData.checkInDate,
          checkOutDate: updatedClickTripzData.checkOutDate,
          destinationId: updatedClickTripzData.destinationId,
          rooms: updatedClickTripzData.rooms
        };

        if (regionInfo) {
          searchData = {
            ...searchData,
            city: regionInfo.city,
            country: regionInfo.countryCode,
            countryCode: regionInfo.countryCode,
            province: regionInfo.state
          };
        }

        clicktripzData = {
          enabled: true,
          verticals: {
            hotel: {
              active: true,
              search: searchData,
              property
            }
          },
          user: {
            id: context && context.deviceId ? context.deviceId.toUpperCase() : '',
            sessionId: sharedGuids && sharedGuids.publisherSessionId ? sharedGuids.publisherSessionId : '',
            loggedIn: updatedClickTripzData && updatedClickTripzData.is_logged_in_user === 'N' ? false : true
          },
          consent: {
            allowed: privacyTrackingConsent(context)
          },
          device: {
            type: updatedClickTripzData && updatedClickTripzData.device,
            browser: updatedClickTripzData && updatedClickTripzData.browser
          },
          site: {
            currentURL: window.location.href,
            pageName: advertising && advertising.triggeredPageName,
            ref: referrerInfo && referrerInfo.referrerChannel
          }
        };

        window[sReload] = clicktripzData;
        if (typeof window[fReload] === 'function') {
          window[fReload]();
        }
      });

      const u = document.getElementsByTagName('script')[0];
      const scr = document.createElement('script');
      scr.type = 'text/javascript';
      scr.src = 'https://static.clicktripz.com/r/ucb.js';
      scr.charset = 'UTF-8';
      scr.async = true;
      scr.setAttribute('data-alias', 'expedia');

      if (u && u.parentNode) {
        u.parentNode.insertBefore(scr, u);
      }
    }
  }

  public componentWillUnmount() {
    if (typeof this.halt === 'function') {
      this.halt();
    }
  }

  public render() {
    return (
      <ErrorBoundary label="meso.click.tripz">
        <meso-click-tripz />
      </ErrorBoundary>
    );
  }
}
